// @flow
import React, {useContext, useEffect, useState} from 'react';
import { renderToString } from 'react-dom/server';
import * as PropTypes from 'prop-types';
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import ArchiverClientApi from "../API/archiverClientApi";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";
import ProfileList from "../GUI_COMMON/COMPONENTS/ProfileList";
import LIButton from "../GUI_COMMON/COMPONENTS/LIButton";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import CheckInput from "../GUI_COMMON/COMPONENTS/CheckInput";
import {StoreContext} from "../GUI_COMMON/SUPPORT/store";
import DigitInput from "../GUI_COMMON/COMPONENTS/DigitInput";

const PrintLabelsForm = ({devices, destinationPrinterDevice}) => {
    
    const ENTIRE_BOX_MAGIC:number=-1;  // special indicator to print an entire box of mag labels
    const DAYS_BEFORE_NEW_YEAR_TO_UPLIFT:number=14; // uplift to next year if less than this many days to new year
    const MAX_LABELS_AT_ONE_TIME:number=10; // max number of labels that can be printed at one time
    
    const [globalState] = useContext(StoreContext);
    const [isPrinting, setIsPrinting] = useState(false);
    const [isLoadingRecentMag, setIsLoadingRecentMag] = useState(false);
    const [deviceInputClass, setDeviceInputClass] = useState("");
    const [deviceValue, setDeviceValue] = useState(-1);
    const [profileValue, setProfileValue] = useState(-1);
    const [showProfilesList, setShowProfilesList] = useState([]);
    const [numToPrintValue, setNumToPrintValue] = useState(ENTIRE_BOX_MAGIC);
    const [mostRecentMagBarcode, setMostRecentMagBarcode] = useState("-");
    const [showUpliftYearCheckbox,setShowUpliftYearCheckbox] = useState(false);
    const [potential2DigitYearForUplift,setPotential2DigitYearForUplift] = useState(0);

    useEffect( () => {
        if(devices.length===1) {
            setDeviceValue(devices[0].id);
        }
    }, [devices]);
    
    useEffect( () => {
        setDeviceInputClass(LIUtils.getValidationClassGivenValue(deviceValue, true, false))
        setProfilesListGivenDevice(deviceValue);
        handleProfileChanged(0); // BUGFIX AR-384 260710 -pott
    },[deviceValue]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        updateMostRecentMagLabel();
    },[profileValue]);  // eslint-disable-line react-hooks/exhaustive-deps
  
    function handleDeviceChanged(e) {
        const value = Number(e.target.value);
        setDeviceValue(value);
        setProfileValue(0); // none - BUGFIX AR-384 260710 -pott
    }
    
    function setProfilesListGivenDevice(deviceId) {
        const device = getDeviceGivenId(deviceId);
        if (!device) {
            setShowProfilesList([]); // none
            return;
        }
        
        const matchingProfilesList = device["profiles"]?.filter(p => p.isMagProfile)??[];
        setShowProfilesList(matchingProfilesList);
        setProfileValue(0); // none
    }
    
    function getDeviceGivenId(deviceId:number):?Object {
        return devices.find(d => d.id===deviceId);
    }
    
    function handleProfileChanged(profileId) {
        const value = Number(profileId);
        if(value===0) {
            setShowUpliftYearCheckbox(false);
            return;
        }
        if(value === profileValue) { // no change
            return;
        }
        setProfileValue(value);
        setShowUpliftYearCheckbox(false);
    }
    
    function updateMostRecentMagLabel() {
        if (!profileIsValid()) {
            setMostRecentMagBarcode("-");
            return;
        }
        setIsLoadingRecentMag(true);
        ArchiverClientApi.getMostRecentRegisteredMagForProfile(profileValue)
        .then(response => {
            setIsLoadingRecentMag(false);
            const magBarcode = response?.barcode;
            const isBoxed = thisProfileMagIsBoxedAndLimitedLabelsPerBox(profileValue);
            let boxStr = "-";
            if (magBarcode) {
                let [yearPlusPnuemonic, , ] = magBarcode.split(".");
                checkForYearUplift(yearPlusPnuemonic);
                boxStr = magBarcode;  // show the entire mag barcode if we're not boxed
            }
            setMostRecentMagBarcode(boxStr);
            if(isBoxed) {
                setNumToPrintValue(ENTIRE_BOX_MAGIC); // set the default to print an entire box
            }
            else {
                setNumToPrintValue(1); // set the default to print one label
            }
        })
        .catch((error:string) => {
            setIsLoadingRecentMag(false);
            setMostRecentMagBarcode("* " + error);
        }); // ignore
    }
    
    function thisProfileMagIsBoxedAndLimitedLabelsPerBox(profile:number):boolean {
        const magType = showProfilesList.filter(p => p.id===profile)[0]?.subType;
        const numMagsPerBox=globalState.magTypes.items.find(m => m.id===magType)?.numMagsPerBox??0;
        return numMagsPerBox>0 && numMagsPerBox<=MAX_LABELS_AT_ONE_TIME;
    }
    
    function checkForYearUplift(yearPlusPnuemonic:string) {
        setShowUpliftYearCheckbox(false);
        if(!profileIsValid() || !deviceIsValid()) {
            return;
        }
        const selectedProfile = showProfilesList.filter(p => p.id===profileValue)[0];
        if(!selectedProfile || LIUtils.magValidationHasHardCodedYear(selectedProfile) ) {
            return;
        }
        const curYear = new Date().getFullYear();
        const cur2DigitYear = curYear-2000;
        const next2DigitYear = cur2DigitYear+1;
        const timeToNewYear = new Date(curYear,11,31).getTime() - new Date().getTime();
        const mag2DigitYear=Number(RegExp(/^(\d{1,2}).*$/).exec(yearPlusPnuemonic)[1]);
        
        if(mag2DigitYear<cur2DigitYear) {
            setShowUpliftYearCheckbox(true);
            if(timeToNewYear < 3600*24*DAYS_BEFORE_NEW_YEAR_TO_UPLIFT*1000) { // less than 24 days to new year and we're in an old year (uplift to next year?)
                setPotential2DigitYearForUplift(next2DigitYear);
            }
            else { // more than 24 days to new year and we're in an old year (uplift to this year?)
                setPotential2DigitYearForUplift(cur2DigitYear);
            }
        }
        else if(mag2DigitYear===cur2DigitYear && timeToNewYear < 3600*24*DAYS_BEFORE_NEW_YEAR_TO_UPLIFT*1000) { // less than 24 days to new year and we're in the current year (uplift to next year?)
            setShowUpliftYearCheckbox(true);
            setPotential2DigitYearForUplift(next2DigitYear);
        }
    }

    function dumpDeviceOptions(devices) {

        if(!devices || devices.length===0) {
            return <option disabled>(no devices available)</option>;
        }

        const optionList = devices.map(item => <option key={item.id} value={item.id}>{item.fullName}</option>);
        optionList.unshift(<option key="-1" value={-1} disabled>(select a device)</option>);
        return optionList;
    }
    function deviceIsValid() {
        return deviceValue > 0;   
    }
    function profileIsValid() {
        return profileValue > 0;
    }
    function numToPrintIsValid() {
        if(!thisProfileMagIsBoxedAndLimitedLabelsPerBox(profileValue)) { // unboxed needs the number of mags to be printed
            return numToPrintValue > 0 && numToPrintValue <= MAX_LABELS_AT_ONE_TIME;
        }
        return true;
    }
    
    function dumpMostRecentMag() {
        return (
            <div className="mt-3">
                <span className="li-font-small mr-2">* Most recent label printed for this profile is </span>
                <span className="badge li-bg-blue-card li-font-small">
                    {
                        isLoadingRecentMag?<LoadingSpinner small center/>:mostRecentMagBarcode
                    }
                </span>
            </div>
        );
    }
    
    function dumpUpliftYearCheckBox() {
        if(!showUpliftYearCheckbox) {
            return null;
        }
        
        return (
            <div className="li-bg-blue-card rounded mt-3 p-1">
                <CheckInput label={"Force year to '"+potential2DigitYearForUplift+"'?"} name="upliftYearCheckbox" showCheckLeft show={showUpliftYearCheckbox}/>
                <span className="li-font-small ml-4">* all future labels will be use this new year</span>
            </div>
        );
    }

    function printerIsValid() {
        return destinationPrinterDevice && destinationPrinterDevice.id > 0;
    }

    function formIsValid() {
        return deviceIsValid() && profileIsValid() && numToPrintIsValid() && printerIsValid();
    }
    
    function onSubmit() {
        setIsPrinting(true);

        let forcing2DigitYearTo: ?number = null;
        if(showUpliftYearCheckbox) {
            const upliftYear:boolean = !!document.getElementById("upliftYearCheckbox")?.checked;
            if(upliftYear) {
                forcing2DigitYearTo = potential2DigitYearForUplift;
            }
        }
        ArchiverClientApi.sendPrintNewLabelsRequest(destinationPrinterDevice.id, numToPrintValue, deviceValue, profileValue, forcing2DigitYearTo)
        .then( (mags:Array<any>) => {
            const numRegistered = `${mags.length} barcode`+(mags.length>1?"s have":" has");
            const barcodesList = mags.map( item => <li>{item.barcode}</li>)
            const msg = `<p>A total of ${numRegistered} been registered.</p>
                <p>Printing should be under way now on printer '${destinationPrinterDevice.fullName}'.</p>`+
                `<ul>${renderToString(barcodesList)}</ul>`+
                "<p>*NOTE: it can take up to 30 seconds to print.</p>";
            
            Popup.showInfo(msg);
            updateMostRecentMagLabel(); // make sure the most recent mag barcode shows the new latest mag.
        })
        .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
        .finally( () => {
            setIsPrinting(false);
        });
        
    }
    
    function dumpNumToPrintIfRequired() {
        if(!profileIsValid()) {
            return null;
        }
        const isBoxed = thisProfileMagIsBoxedAndLimitedLabelsPerBox(profileValue);
        if(!isBoxed) {
            return (
                <div className="mt-3">
                    <DigitInput onChange={(e)=>setNumToPrintValue(Number(e.target.value))} label="Number of labels to print?" name="numLabelsToPrint" minValue={1}
                                maxValue={10} value={numToPrintValue} goodNess={(numToPrintValue<=MAX_LABELS_AT_ONE_TIME && numToPrintValue>0)?"good":"bad"} stepValue={1} 
                                required={true} oneRow inputWidthPels={100}
                                helpText="* max of 10 at a time"/>
                
                </div>
            );
        }
        return null;
    }

    return (
        <form id="printLabelsForm">
           
            <div className="card border-0 mt-4">
                <h2>Print New Labels</h2>
                <p>Use this to print labels for new magazines that have not yet been assigned to any devices.</p>
                <ul className="card li-bg-blue-card">
                    <li className="li-font-small">if required, a new box label will be automatically be printed for each new box.</li>
                </ul>
                
                <div className="row">
                    <div className="col-md-4">
                        <label className="li-form-label" htmlFor="deviceSelect">For Device</label>
                        <select onChange={handleDeviceChanged} id="deviceSelect" name="deviceSelect" 
                                value={deviceValue??-1} className={deviceInputClass} required={true}
                                disabled={false}>
                            {dumpDeviceOptions(devices)}
                        </select>
                    </div>

                    <div className="offset-md-1 col-md-7 form-group">
                        <label className="li-form-label" htmlFor="profileSelect">For Profile</label>
                        <ProfileList profiles={showProfilesList} magOnly selectedId={profileValue} onRadioChanged={(profileId) => handleProfileChanged(profileId)}/>
                        {dumpMostRecentMag()}
                        {dumpUpliftYearCheckBox()}
                        {dumpNumToPrintIfRequired()}
                    </div>
                </div>
                
                <div className="row justify-content-center">
                    <LIButton onClick={onSubmit} disabled={!formIsValid()} primary bgClass="my-4 px-5"
                              label="Print Labels" large showBusy={isPrinting}/>
                </div>
            
            </div>
            
           
        </form>
    );
};

PrintLabelsForm.propTypes = {
    devices: PropTypes.array.isRequired,
    destinationPrinterDevice: PropTypes.object.isRequired
};

export default PrintLabelsForm;